import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useNotifications, UserNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole } from '../../types/user.types';
import { LeadNotificationPopup } from './LeadNotificationPopup';
import { CRM_CONTACTS_PATH, crmContactPathOrList } from '../../lib/crm-v2/contactRoutes';

// One compact toast at a time — additional arrivals collapse into the
// "+N more new leads" counter on the visible card. The old 3-wide stack
// blocked page content (consultant complaint, 2026-08-05 redesign).
const MAX_QUEUE = 10;

interface QueueItem {
  notification: UserNotification;
  addedAt: number;
}

/**
 * Per-user localStorage baseline: notifications created at or before this
 * timestamp never pop again (reloads/re-subscribes can't replay old leads).
 * Belt-and-braces with NotificationContext's initial-snapshot skip.
 * App-level analogue of crmV2.lastSeenLeadNotifAt.v1.
 */
const baselineKey = (userId: string) => `pc.lastSeenLeadNotifAt:${userId}`;

function readBaseline(userId: string | undefined): number {
  if (!userId) return 0;
  try {
    return Number(localStorage.getItem(baselineKey(userId))) || 0;
  } catch {
    return 0;
  }
}

function advanceBaseline(userId: string | undefined, toMillis: number): void {
  if (!userId) return;
  try {
    const current = Number(localStorage.getItem(baselineKey(userId))) || 0;
    if (toMillis > current) localStorage.setItem(baselineKey(userId), String(toMillis));
  } catch {
    // storage unavailable — the snapshot-skip in NotificationContext still guards
  }
}

/**
 * LeadNotificationStack — renders at app level, shows at most ONE lead
 * assignment toast. Registers a callback with NotificationContext to
 * receive new_lead_assigned notifications.
 */
export function LeadNotificationStack() {
  const { registerLeadNotificationCallback, unregisterLeadNotificationCallback, markAsRead } = useNotifications();
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Suppress the toast where a dedicated non-intrusive surface exists:
  //  - the v2 CRM (top-of-Inbox banner, tab-title flash, OS notifications)
  //  - the consultant dashboard (inline "New leads" panel shows the same feed)
  const isV2Active = location.pathname.startsWith('/crm-v2');
  const isConsultantDashboard =
    location.pathname === '/dashboard' &&
    (userProfile?.role === UserRole.HQ_CONSULTANT_LVL1 ||
      userProfile?.role === UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2);

  const [queue, setQueue] = useState<QueueItem[]>([]);

  // Handle incoming lead notifications
  const handleLeadNotification = useCallback((notification: UserNotification) => {
    const now = Date.now();
    const createdMillis = notification.createdAt?.toMillis?.() ?? now;

    // Anything at or before the per-user baseline has been shown before.
    if (createdMillis <= readBaseline(userProfile?.id)) return;
    advanceBaseline(userProfile?.id, createdMillis);

    setQueue(prev => {
      if (prev.some(q => q.notification.id === notification.id)) return prev;
      return [...prev, { notification, addedAt: now }].slice(-MAX_QUEUE);
    });
  }, [userProfile?.id]);

  // Register/unregister callback
  useEffect(() => {
    registerLeadNotificationCallback(handleLeadNotification);
    return () => unregisterLeadNotificationCallback();
  }, [registerLeadNotificationCallback, unregisterLeadNotificationCallback, handleLeadNotification]);

  // Dismissing the visible toast reveals the next queued lead (if any).
  const handleDismiss = useCallback((id: string) => {
    setQueue(prev => prev.filter(q => q.notification.id !== id));
  }, []);

  // View lead — navigate + mark as read
  const handleViewLead = useCallback((notification: UserNotification) => {
    markAsRead(notification.id);
    const contactId = notification.data?.contactId;
    navigate(crmContactPathOrList(contactId));
    setQueue(prev => prev.filter(q => q.notification.id !== notification.id));
  }, [markAsRead, navigate]);

  // "+N more — View all" — clear the toast queue and go to the contacts
  // list. The leads stay unread in the bell until actually opened.
  const handleViewAll = useCallback(() => {
    setQueue([]);
    navigate(CRM_CONTACTS_PATH);
  }, [navigate]);

  const current = queue[0];

  if (!current) return null;
  if (isV2Active || isConsultantDashboard) return null;

  return (
    <LeadNotificationPopup
      key={current.notification.id}
      notification={current.notification}
      onDismiss={handleDismiss}
      onViewLead={handleViewLead}
      moreCount={queue.length - 1}
      onViewAll={handleViewAll}
    />
  );
}
